import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "../../lib/supabase";

interface ImageUploaderProps {
  images: string[];
  onChange: (urls: string[]) => void;
  maxImages?: number;
  folder?: string;
  label?: string;
}

// ─── Types ───────────────────────────────────────────────────────────────────
type AspectKey = "16:9" | "4:3" | "1:1" | "free";
const ASPECT_PRESETS: { key: AspectKey; label: string; ratio: number | null }[] = [
  { key: "16:9", label: "16:9", ratio: 16 / 9 },
  { key: "4:3", label: "4:3", ratio: 4 / 3 },
  { key: "1:1", label: "1:1", ratio: 1 },
  { key: "free", label: "Tự do", ratio: null },
];

interface CropBox { x: number; y: number; w: number; h: number; }
type ResizeHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";
const RESIZE_HANDLES: ResizeHandle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

// ─── Visual Crop Modal (Canva-style) ─────────────────────────────────────────
interface CropModalProps {
  src: string;
  folder: string;
  onConfirm: (url: string) => void;
  onCancel: () => void;
}

const CropModal: React.FC<CropModalProps> = ({ src, folder, onConfirm, onCancel }) => {
  const [aspectKey, setAspectKey] = useState<AspectKey>("16:9");
  const [zoom, setZoom] = useState(1);
  const [imgOffset, setImgOffset] = useState({ x: 0, y: 0 });
  const [cropBox, setCropBox] = useState<CropBox>({ x: 10, y: 10, w: 80, h: 60 });
  // dragging: null | "img" | "crop" | ResizeHandle
  const [dragging, setDragging] = useState<"img" | "crop" | ResizeHandle | null>(null);
  const [dragStart, setDragStart] = useState({ mx: 0, my: 0, box: cropBox, ox: 0, oy: 0 });
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const currentAspect = ASPECT_PRESETS.find((p) => p.key === aspectKey)?.ratio ?? null;
  const cropH = currentAspect !== null ? cropBox.w / currentAspect : cropBox.h;

  useEffect(() => {
    if (currentAspect !== null) {
      setCropBox((prev) => {
        const newH = prev.w / currentAspect;
        const clampedH = Math.min(newH, 90);
        const clampedW = clampedH * currentAspect;
        return { ...prev, w: Math.min(clampedW, 90), h: clampedH };
      });
    }
  }, [aspectKey]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
  }, [src]);

  // ── Mouse handlers ──
  const handleMouseDown = (e: React.MouseEvent, type: "img" | "crop" | ResizeHandle) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(type);
    setDragStart({ mx: e.clientX, my: e.clientY, box: { ...cropBox }, ox: imgOffset.x, oy: imgOffset.y });
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragStart.mx) / rect.width) * 100;
      const dy = ((e.clientY - dragStart.my) / rect.height) * 100;

      if (dragging === "img") {
        setImgOffset({ x: dragStart.ox + dx, y: dragStart.oy + dy });
        return;
      }
      if (dragging === "crop") {
        const newX = Math.max(0, Math.min(100 - dragStart.box.w, dragStart.box.x + dx));
        const newY = Math.max(0, Math.min(100 - cropH, dragStart.box.y + dy));
        setCropBox((prev) => ({ ...prev, x: newX, y: newY }));
        return;
      }
      // Resize handles
      const b = dragStart.box;
      let { x, y, w, h } = b;
      if (dragging.includes("e")) w = Math.max(10, Math.min(100 - b.x, b.w + dx));
      if (dragging.includes("w")) { x = Math.max(0, Math.min(b.x + b.w - 10, b.x + dx)); w = b.x + b.w - x; }
      if (dragging.includes("s") && currentAspect === null) h = Math.max(10, Math.min(100 - b.y, b.h + dy));
      if (dragging.includes("n") && currentAspect === null) { y = Math.max(0, Math.min(b.y + b.h - 10, b.y + dy)); h = b.y + b.h - y; }
      if (currentAspect !== null) h = w / currentAspect;
      setCropBox({ x, y, w, h });
    };
    const onUp = () => setDragging(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging, dragStart, currentAspect, cropH]);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((prev) => Math.max(0.5, Math.min(4, prev - e.deltaY * 0.001)));
  };

  // ── Live preview ──
  const generatePreview = useCallback(() => {
    if (!imgRef.current || !containerRef.current || !canvasRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const imgEl = imgRef.current;
    const imgRect = imgEl.getBoundingClientRect();
    const scaleX = imgNatural.w / imgRect.width;
    const scaleY = imgNatural.h / imgRect.height;
    const imgOffsetXPx = imgRect.left - container.left;
    const imgOffsetYPx = imgRect.top - container.top;
    const cropPxX = (cropBox.x / 100) * container.width - imgOffsetXPx;
    const cropPxY = (cropBox.y / 100) * container.height - imgOffsetYPx;
    const cropPxW = (cropBox.w / 100) * container.width;
    const cropPxH = (cropH / 100) * container.height;
    const srcX = cropPxX * scaleX;
    const srcY = cropPxY * scaleY;
    const srcW = cropPxW * scaleX;
    const srcH = cropPxH * scaleY;
    const outW = Math.min(800, Math.max(200, Math.round(srcW)));
    const outH = currentAspect !== null ? Math.round(outW / currentAspect) : Math.round(srcH);
    const canvas = canvasRef.current;
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.clearRect(0, 0, outW, outH);
      ctx.drawImage(img, Math.max(0, srcX), Math.max(0, srcY), srcW, srcH, 0, 0, outW, outH);
      setPreviewUrl(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = src;
  }, [cropBox, cropH, imgNatural, currentAspect, src]);

  useEffect(() => {
    const timer = setTimeout(generatePreview, 150);
    return () => clearTimeout(timer);
  }, [cropBox, zoom, imgOffset, generatePreview]);

  // ── Confirm & upload ──
  const handleConfirm = async () => {
    if (!imgRef.current || !containerRef.current || !canvasRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const imgEl = imgRef.current;
    const imgRect = imgEl.getBoundingClientRect();
    const scaleX = imgNatural.w / imgRect.width;
    const scaleY = imgNatural.h / imgRect.height;
    const imgOffsetXPx = imgRect.left - container.left;
    const imgOffsetYPx = imgRect.top - container.top;
    const cropPxX = (cropBox.x / 100) * container.width - imgOffsetXPx;
    const cropPxY = (cropBox.y / 100) * container.height - imgOffsetYPx;
    const cropPxW = (cropBox.w / 100) * container.width;
    const cropPxH = (cropH / 100) * container.height;
    const srcX = cropPxX * scaleX;
    const srcY = cropPxY * scaleY;
    const srcW = cropPxW * scaleX;
    const srcH = cropPxH * scaleY;
    const outW = Math.min(1920, Math.max(400, Math.round(srcW)));
    const outH = currentAspect !== null ? Math.round(outW / currentAspect) : Math.round(srcH);
    const canvas = canvasRef.current;
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    setUploading(true);
    setProgress(10);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = async () => {
      ctx.drawImage(img, Math.max(0, srcX), Math.max(0, srcY), srcW, srcH, 0, 0, outW, outH);
      canvas.toBlob(async (blob) => {
        if (!blob) { setUploading(false); return; }
        setProgress(40);
        const fileName = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.webp`;
        const { data, error } = await supabase.storage
          .from("bizwell-images")
          .upload(fileName, blob, { contentType: "image/webp", upsert: false });
        setProgress(90);
        if (!error && data) {
          const { data: urlData } = supabase.storage.from("bizwell-images").getPublicUrl(data.path);
          onConfirm(urlData.publicUrl + `?t=${Date.now()}`);
        }
        setUploading(false);
      }, "image/webp", 0.92);
    };
    img.src = src;
  };

  const posMap: Record<ResizeHandle, string> = {
    nw: "top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nw-resize",
    n: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-n-resize",
    ne: "top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-ne-resize",
    e: "top-1/2 right-0 translate-x-1/2 -translate-y-1/2 cursor-e-resize",
    se: "bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-se-resize",
    s: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-s-resize",
    sw: "bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-sw-resize",
    w: "top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 cursor-w-resize",
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/85 flex items-center justify-center p-4">
      <canvas ref={canvasRef} className="hidden" />
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-800 text-sm">Visual Editor — Cắt & Chỉnh sửa ảnh</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Kéo ảnh để di chuyển · Kéo góc khung vàng để resize · Cuộn chuột để zoom
            </p>
          </div>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {/* Aspect ratio */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-shrink-0 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 mr-1">Tỷ lệ:</span>
          {ASPECT_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setAspectKey(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                aspectKey === p.key ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              const w = cropBox.w;
              const h = currentAspect !== null ? w / currentAspect : cropBox.h;
              setCropBox({ x: (100 - w) / 2, y: (100 - h) / 2, w, h });
            }}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-focus-3-line" />Căn giữa
          </button>
        </div>

        {/* Main area */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Crop canvas */}
          <div className="flex-1 p-4 overflow-hidden">
            <div
              ref={containerRef}
              className="relative w-full h-full rounded-xl overflow-hidden bg-gray-900 select-none"
              style={{ minHeight: 280, cursor: dragging === "img" ? "grabbing" : "grab" }}
              onMouseDown={(e) => handleMouseDown(e, "img")}
              onWheel={handleWheel}
            >
              <img
                ref={imgRef}
                src={src}
                alt="Crop"
                className="absolute pointer-events-none"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  transform: `scale(${zoom}) translate(${imgOffset.x / zoom}%, ${imgOffset.y / zoom}%)`,
                  transformOrigin: "center center",
                }}
                draggable={false}
                crossOrigin="anonymous"
              />
              {/* Dark overlay */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `
                    linear-gradient(to right, rgba(0,0,0,0.55) ${cropBox.x}%, transparent ${cropBox.x}%),
                    linear-gradient(to left, rgba(0,0,0,0.55) ${100 - cropBox.x - cropBox.w}%, transparent ${100 - cropBox.x - cropBox.w}%),
                    linear-gradient(to bottom, rgba(0,0,0,0.55) ${cropBox.y}%, transparent ${cropBox.y}%),
                    linear-gradient(to top, rgba(0,0,0,0.55) ${100 - cropBox.y - cropH}%, transparent ${100 - cropBox.y - cropH}%)
                  `,
                }}
              />
              {/* Crop box */}
              <div
                className="absolute border-2 border-yellow-400 z-10 cursor-move"
                style={{ left: `${cropBox.x}%`, top: `${cropBox.y}%`, width: `${cropBox.w}%`, height: `${cropH}%` }}
                onMouseDown={(e) => handleMouseDown(e, "crop")}
              >
                {/* Rule of thirds */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/25" />
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/25" />
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-white/25" />
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-white/25" />
                </div>
                {/* Resize handles */}
                {RESIZE_HANDLES.map((handle) => (
                  <div
                    key={handle}
                    className={`absolute w-3.5 h-3.5 bg-yellow-400 rounded-sm transform ${posMap[handle]} z-20 hover:bg-yellow-300 transition-colors`}
                    onMouseDown={(e) => handleMouseDown(e, handle)}
                  />
                ))}
                {/* Drag hint */}
                <div className="absolute top-1 right-1 bg-yellow-400/90 text-black text-[8px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none">
                  KÉO ĐỂ DI CHUYỂN
                </div>
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="w-52 flex-shrink-0 p-4 border-l border-gray-100 flex flex-col gap-4 overflow-y-auto">
            {/* Zoom */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-gray-600">Zoom</span>
                <span className="text-xs font-bold text-[#004A99]">{zoom.toFixed(1)}x</span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                  <i className="ri-subtract-line text-xs" />
                </button>
                <input type="range" min={0.5} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="flex-1 accent-[#004A99]" />
                <button type="button" onClick={() => setZoom((z) => Math.min(4, z + 0.1))} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                  <i className="ri-add-line text-xs" />
                </button>
              </div>
              <button type="button" onClick={() => { setZoom(1); setImgOffset({ x: 0, y: 0 }); }} className="mt-1 text-[10px] text-gray-400 hover:text-gray-600 cursor-pointer">
                Reset zoom & vị trí
              </button>
            </div>

            {/* Live Preview */}
            <div>
              <div className="flex items-center gap-1 mb-1.5">
                <span className="text-xs font-semibold text-gray-600">Xem trước</span>
                <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">LIVE</span>
              </div>
              {previewUrl ? (
                <div className="rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                  <img src={previewUrl} alt="Preview" className="w-full object-cover" />
                </div>
              ) : (
                <div className="rounded-lg bg-gray-100 flex items-center justify-center h-20 text-gray-400 text-xs">
                  Đang tạo...
                </div>
              )}
            </div>

            {/* Image info */}
            {imgNatural.w > 0 && (
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-[10px] font-semibold text-gray-600 mb-1">Thông tin ảnh</p>
                <p className="text-[10px] text-gray-500">{imgNatural.w} × {imgNatural.h}px</p>
                {imgNatural.w < 1200 && (
                  <p className="text-[10px] text-amber-600 mt-1">
                    <i className="ri-alert-line mr-0.5" />Độ phân giải thấp
                  </p>
                )}
              </div>
            )}

            {/* Tips */}
            <div className="bg-[#EDF2FB] rounded-lg p-2.5">
              <p className="text-[10px] font-semibold text-[#004A99] mb-1">Mẹo sử dụng</p>
              <ul className="text-[10px] text-[#004A99]/70 space-y-1">
                <li>• Kéo ảnh để di chuyển</li>
                <li>• Kéo góc vàng để resize</li>
                <li>• Cuộn chuột để zoom</li>
                <li>• Kéo khung vàng để dời vùng cắt</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-gray-400">
            <i className="ri-drag-move-line mr-1" />
            Kéo góc khung vàng để resize · Kéo ảnh để di chuyển
          </p>
          <div className="flex gap-3">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">
              Hủy
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={uploading}
              className="flex items-center gap-2 px-5 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#003a7a] transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <><i className="ri-loader-4-line animate-spin" />Đang lưu... {progress}%</>
              ) : (
                <><i className="ri-crop-line" />Xác nhận & Lưu</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main ImageUploader ───────────────────────────────────────────────────────
const ImageUploader = ({
  images,
  onChange,
  maxImages = 6,
  folder = "products",
  label = "Ảnh",
}: ImageUploaderProps) => {
  const [uploading, setUploading] = useState<boolean[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropTargetIdx, setCropTargetIdx] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cropInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File): Promise<string | null> => {
    const ext = file.name.split(".").pop();
    const fileName = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { data, error } = await supabase.storage
      .from("bizwell-images")
      .upload(fileName, file, { contentType: file.type, upsert: false });
    if (error || !data) return null;
    const { data: urlData } = supabase.storage.from("bizwell-images").getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const remaining = maxImages - images.length;
      if (remaining <= 0) return;
      const toUpload = Array.from(files).slice(0, remaining);
      const newUploading = toUpload.map(() => true);
      setUploading(newUploading);
      const uploaded: string[] = [];
      for (let i = 0; i < toUpload.length; i++) {
        const url = await uploadFile(toUpload[i]);
        if (url) uploaded.push(url);
        setUploading((prev) => prev.map((u, idx) => (idx === i ? false : u)));
      }
      if (uploaded.length > 0) onChange([...images, ...uploaded]);
      setUploading([]);
    },
    [images, maxImages, onChange, folder]
  );

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeImage = (idx: number) => {
    const next = [...images];
    next.splice(idx, 1);
    onChange(next);
  };

  const openCropForExisting = (idx: number) => {
    setCropTargetIdx(idx);
    setCropSrc(images[idx]);
  };

  const handleCropFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const url = URL.createObjectURL(file);
    setCropTargetIdx(null);
    setCropSrc(url);
  };

  const handleCropConfirm = (url: string) => {
    if (cropTargetIdx !== null) {
      const next = [...images];
      next[cropTargetIdx] = url;
      onChange(next);
    } else {
      if (images.length < maxImages) onChange([...images, url]);
    }
    if (cropSrc && cropSrc.startsWith("blob:")) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setCropTargetIdx(null);
    if (cropInputRef.current) cropInputRef.current.value = "";
  };

  const handleCropCancel = () => {
    if (cropSrc && cropSrc.startsWith("blob:")) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setCropTargetIdx(null);
    if (cropInputRef.current) cropInputRef.current.value = "";
  };

  const isUploading = uploading.some(Boolean);
  const canAdd = images.length < maxImages;

  return (
    <div>
      {cropSrc && (
        <CropModal
          src={cropSrc}
          folder={folder}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}

      <label className="block text-xs font-semibold text-gray-600 mb-2">
        {label} ({images.length}/{maxImages})
      </label>

      {/* Preview grid */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-3">
          {images.map((url, i) => (
            <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-gray-200 group">
              <img src={url} alt={`image-${i}`} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => openCropForExisting(i)}
                  className="w-7 h-7 bg-white/90 text-[#004A99] rounded-lg flex items-center justify-center cursor-pointer hover:bg-white"
                  title="Visual Editor — Cắt & chỉnh sửa"
                >
                  <i className="ri-drag-move-line text-xs" />
                </button>
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="w-7 h-7 bg-red-500 text-white rounded-lg flex items-center justify-center cursor-pointer hover:bg-red-600"
                  title="Xóa"
                >
                  <i className="ri-close-line text-xs" />
                </button>
              </div>
              {i === 0 && (
                <span className="absolute bottom-1 left-1 text-[9px] bg-[#004A99] text-white px-1.5 py-0.5 rounded font-bold">
                  Chính
                </span>
              )}
            </div>
          ))}
          {isUploading &&
            uploading.map((u, i) =>
              u ? (
                <div key={`upload-${i}`} className="w-24 h-24 rounded-xl border-2 border-dashed border-[#004A99]/30 flex items-center justify-center bg-[#F0F6FF]">
                  <i className="ri-loader-4-line text-[#004A99] text-xl animate-spin" />
                </div>
              ) : null
            )}
        </div>
      )}

      {/* Upload zone */}
      {canAdd && (
        <div className="flex gap-2">
          {/* Direct upload */}
          <div
            className={`relative flex-1 border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer ${
              dragOver ? "border-[#004A99] bg-[#EDF2FB]" : "border-gray-200 hover:border-[#004A99]/50 hover:bg-gray-50"
            }`}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => inputRef.current?.click()}
          >
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            <div className="w-8 h-8 flex items-center justify-center bg-[#EDF2FB] rounded-xl mx-auto mb-2">
              <i className="ri-image-add-line text-lg text-[#004A99]" />
            </div>
            <p className="text-xs font-semibold text-gray-700">
              {isUploading ? "Đang tải lên..." : "Kéo thả hoặc bấm để chọn"}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">JPG, PNG, WebP · Tối đa {maxImages} ảnh</p>
          </div>

          {/* Upload with Visual Editor */}
          <div
            className="border-2 border-dashed border-amber-300 rounded-xl p-4 text-center transition-all cursor-pointer hover:border-amber-400 hover:bg-amber-50 flex flex-col items-center justify-center gap-1 min-w-[90px]"
            onClick={() => cropInputRef.current?.click()}
          >
            <input ref={cropInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleCropFileSelect(e.target.files)} />
            <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-xl">
              <i className="ri-drag-move-line text-lg text-amber-600" />
            </div>
            <p className="text-[10px] font-semibold text-amber-700 whitespace-nowrap">Visual Edit</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
